import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { Textarea } from "../../../../components/ui/textarea";
import { Label } from "../../../../components/ui/label";
import { Citation } from "../../../../models/audit/citation";

interface EditCitationSummaryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  citation: Citation | null;
  onSave: (citationId: string, newSummary: string) => Promise<void>;
  onDelete?: (citationId: string) => Promise<void>;
}

export const EditCitationSummaryModal = ({
  open,
  onOpenChange,
  citation,
  onSave,
  onDelete,
}: EditCitationSummaryModalProps) => {
  const [summary, setSummary] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    // Clear the inline delete-confirm state whenever the modal opens/closes or
    // the citation changes, so a stale confirm dialog can't linger without a
    // valid citation (which would make the Delete button a no-op).
    setShowDeleteConfirm(false);
    setIsDeleting(false);
    if (open && citation) {
      setSummary(citation.shortSummary || "");
    }
  }, [open, citation]);

  const handleSubmit = async () => {
    if (!citation?.id || !summary.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave(citation.id, summary.trim());
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSummary("");
    onOpenChange(false);
  };

  const handleDeleteConfirm = async () => {
    if (!citation?.id || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(citation.id);
      setShowDeleteConfirm(false);
      onOpenChange(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Evidence Summary</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">
              Update the summary for this citation
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-summary">
                Summary <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="edit-summary"
                placeholder="Describe what this citation shows..."
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting || isDeleting}
                aria-label="Delete citation"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting || isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!summary.trim() || isSubmitting || isDeleting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this citation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the citation from the evidence walkthrough. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
